import {
  CatalogCategoryType,
  FilteredCategoryItemGroup,
} from '@/contexts/item-context-types';
import {
  CategoryListReturnType,
  getVariantPackGroupParentUUIDs,
} from '@/utils/category';
import { useCatalogCategoryList } from '@/hooks/use-catalog-category-list';
import { useEffect, useMemo, useState } from 'react';
import { useItemGroupCategoryFilters } from '@/hooks/use-item-group-category-filters';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export interface Props {
  category?: CatalogCategoryType;
}

export interface CategoryCatalogReturn extends CategoryListReturnType {
  subcategories: FilteredCategoryItemGroup[];
}

export const useFilteredCategoryCatalog = ({ category }: Props) => {
  const [badgesFilter, setBadgesFilter] = useState<Set<string>>(new Set());
  const [subcategoriesFilter, setSubcategoriesFilter] = useState<Set<string>>(
    new Set(),
  );

  const { variantPackGroupMap, parentVariantPackGroupMap } =
    useVariantPackInfoContext();

  // Process the subcategoriesFilter to extract last uuid in the path
  const selectedSubcategoryUUIDs = useMemo(() => {
    return new Set(
      Array.from(subcategoriesFilter).map((path) => {
        const segments = path.split('/');
        return segments[segments.length - 1];
      }),
    );
  }, [subcategoriesFilter]);

  const preserveSubcategoryUUIDs = useMemo(() => {
    if (selectedSubcategoryUUIDs.size === 0) {
      return undefined;
    }
    // Preserve selected subcategories and their parent chain
    const selectedUUIDsArray = Array.from(selectedSubcategoryUUIDs);
    const parentUUIDs = getVariantPackGroupParentUUIDs(
      selectedUUIDsArray,
      parentVariantPackGroupMap,
    );
    return new Set(parentUUIDs);
  }, [selectedSubcategoryUUIDs, parentVariantPackGroupMap]);

  const categoryCatalog = useCatalogCategoryList({
    badgesFilter,
    category,
    selectedSubcategoryUUIDs,
  });

  const { categoryItemGroupsInSelectorWithoutAllFilter } =
    useItemGroupCategoryFilters({
      categoryUUIDsToFilterFrom:
        variantPackGroupMap[category?.uuid!]?.directSubgroupUUIDs ?? [],
      excludeExtraCategories: false,
      filterByCategoryType: false,
      preserveSubcategoryUUIDs,
      selectedCategoryUUID: category?.uuid,
      subcategoryItemCounts: categoryCatalog.subcategoryItemCounts,
    });

  useEffect(() => {
    categoryCatalog?.gtinFilters.resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  return {
    badgesFilter,
    setBadgesFilter,
    setSubcategoriesFilter,
    subcategories: categoryItemGroupsInSelectorWithoutAllFilter,
    subcategoriesFilter,
    ...categoryCatalog,
  };
};
